/**
 * «Публикации» — две вкладки (задача Оли №376, адрес — `web/src/lib/postsTabs.ts`): «Черновики»
 * (создание «Новая публикация», правка — клик по плитке, окно «Шаблоны» с `PromptsTab`; удаления на
 * плитке нет — №378, «слишком рискованно»: оно в шапке открытой публикации) и «Контент-план»
 * (`ContentPlanTab`: одна соцсеть за раз — переключатель сетей в этой же строке, `?net=`, №383; окно
 * «Аккаунты» с `AccountsTab`). Заголовка нет — раздел назван в шапке приложения.
 *
 * Утверждают СЕТЬ (№285), поэтому на плитке у утверждённой сети стоит «✓», а частично утверждённая
 * публикация остаётся в «Черновиках» с подписью «утверждено частично: … · ещё N в черновике»: работа
 * не закончена, и прятать её в «Утверждено» было бы неправдой.
 */
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { AccountsTab } from "@/components/posts/AccountsTab";
import { CandidatePicker } from "@/components/posts/CandidatePicker";
import { ContentPlanTab } from "@/components/posts/ContentPlanTab";
import { PromptsTab } from "@/components/posts/PromptsTab";
import { RetryImg } from "@/components/trends/PinGrid";
import { Dialog } from "@/components/trends/Dialog";
import { btn, btnPrimary, Empty, ErrorLine, Loading, Segmented } from "@/components/trends/ui";
import { thumbUrl, when } from "@/lib/media";
import { NETWORK_LABEL, partialNote } from "@/lib/postPlan";
import { loadContentPlan, loadPlans, postsKeys } from "@/lib/postsApi";
import { parseTabs, planNet, tabsSearch, type PostsDialog, type PostsTab } from "@/lib/postsTabs";

function DraftList() {
  const status = "draft";
  const q = useQuery({ queryKey: postsKeys.plans(status), queryFn: () => loadPlans(status) });
  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorLine error={q.error} />;
  if (q.data.length === 0) return <Empty>Черновиков нет.</Empty>;
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-3">
      {q.data.map((p) => {
        const approved = new Set(p.approved_networks);
        const partial = partialNote(p.approved_networks, p.networks);
        return (
          <Link
            key={p.id}
            to={`/posts/${p.id}`}
            className="flex h-full flex-col gap-1 rounded-[var(--radius-card)] border border-border bg-bg p-1.5 transition-colors hover:border-accent"
          >
            <div className="relative">
              {p.video_ms !== null ? (
                p.poster_path ? (
                  <RetryImg src={thumbUrl(p.poster_path)} alt="" className="aspect-[3/4] w-full rounded-[var(--radius-ui)] object-cover" />
                ) : (
                  <div className="grid aspect-[3/4] w-full place-items-center rounded-[var(--radius-ui)] bg-bg-subtle text-[11px] text-fg-faint">
                    нет постера
                  </div>
                )
              ) : (
                <RetryImg src={thumbUrl(p.asset_path)} alt="" className="aspect-[3/4] w-full rounded-[var(--radius-ui)] object-cover" />
              )}
              {p.media_count > 1 ? (
                <span className="absolute bottom-1 right-1 rounded-[var(--radius-ui)] bg-black/60 px-1 text-[10px] text-white">
                  +{p.media_count - 1}
                </span>
              ) : null}
            </div>
            <span className="text-[12.5px] text-fg">
              №{p.id} · {p.series ? `серия ${p.series}` : "нет серии CRM"}
            </span>
            <span className="truncate text-[11.5px] text-fg-muted" data-annot="сети публикации">
              {p.networks.length > 0
                ? p.networks.map((n) => NETWORK_LABEL[n] + (approved.has(n) ? " ✓" : "")).join(" · ")
                : "сети не выбраны"}
            </span>
            {partial ? <span className="truncate text-[11px] text-warn">{partial}</span> : null}
            <span className="text-[11.5px] text-fg-faint">аккаунтов: {p.accounts}</span>
            <span className="truncate text-[11px] text-fg-faint">
              {p.author_name ?? "—"} · {when(p.date_modify)}
            </span>
          </Link>
        );
      })}
    </div>
  );
}

export function PostsPage() {
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();
  const { tab, open } = parseTabs(search);
  const [picking, setPicking] = useState(false);
  const plan = useQuery({ queryKey: postsKeys.contentPlan, queryFn: loadContentPlan, enabled: tab === "plan" });
  const nets = plan.data?.networks ?? [];
  const net = planNet(search, nets);
  const network = nets.find((n) => n.code === net) ?? null;
  // сеть контент-плана держится в адресе и при открытии окна «Аккаунты»
  const go = (t: PostsTab, o: PostsDialog = null, code: string | null = net) =>
    setSearch({ ...tabsSearch(t, o), ...(t === "plan" && code ? { net: code } : {}) }, { replace: true });
  // сеть по умолчанию — один раз в адрес: иначе после отметки выкладки последнего отложенного поста
  // экран сам перескочил бы на другую сеть с отложенными
  useEffect(() => {
    if (tab === "plan" && net && !search.get("net")) {
      setSearch(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("net", net);
          return next;
        },
        { replace: true },
      );
    }
  }, [tab, net, search, setSearch]);

  return (
    <div className="flex flex-col gap-4 p-5">
      <div className="flex flex-wrap items-center gap-3">
        <Segmented<PostsTab>
          label=""
          annot="вкладки публикаций"
          value={tab}
          onPick={(t) => go(t)}
          options={[
            { value: "drafts", label: "Черновики" },
            { value: "plan", label: "Контент-план" },
          ]}
        />
        {tab === "drafts" ? (
          <>
            <button type="button" className={`${btn} ml-auto`} onClick={() => go("drafts", "prompts")}>
              Шаблоны
            </button>
            <button type="button" className={btnPrimary} onClick={() => setPicking(true)}>
              <Plus size={14} /> Новая публикация
            </button>
          </>
        ) : (
          <>
            {net ? (
              <Segmented<string>
                label=""
                annot="сети контент-плана"
                value={net}
                onPick={(code) => go("plan", null, code)}
                options={nets.map((n) => ({ value: n.code, label: n.name }))}
              />
            ) : null}
            <button type="button" className={`${btn} ml-auto`} onClick={() => go("plan", "accounts")}>
              Аккаунты
            </button>
          </>
        )}
      </div>
      {tab === "drafts" ? (
        <DraftList />
      ) : plan.isError ? (
        <ErrorLine error={plan.error} />
      ) : plan.isPending ? (
        <Loading />
      ) : network ? (
        <ContentPlanTab network={network} />
      ) : (
        <Empty>Сетей нет.</Empty>
      )}
      <Dialog wide locked open={open === "prompts"} title="Шаблоны текстов" onClose={() => go(tab)}>
        <div className="p-4">
          <PromptsTab />
        </div>
      </Dialog>
      <Dialog wide locked open={open === "accounts"} title="Аккаунты соцсетей" onClose={() => go(tab)}>
        <div className="p-4">
          <AccountsTab />
        </div>
      </Dialog>
      <CandidatePicker
        open={picking}
        onClose={() => setPicking(false)}
        onCreated={(id) => {
          setPicking(false);
          navigate(`/posts/${id}`);
        }}
      />
    </div>
  );
}
